/**
 * Copy text without throwing.
 *
 * `navigator.clipboard.writeText` rejects with NotAllowedError when the
 * document is not focused — tab blur during an awaited mutation, Electron
 * window focus, or a permission prompt. Callers that fire-and-forget that
 * promise become unhandledrejections (ShareDialog create-link). Always await
 * this helper instead of calling `writeText` directly.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the execCommand path
    }
  }

  return copyTextViaExecCommand(text);
}

function copyTextViaExecCommand(text: string): boolean {
  if (typeof document === "undefined") return false;

  // Mount inside the open modal dialog (if any). A textarea appended to
  // `document.body` sits outside a Radix modal's focus trap, which pulls focus
  // straight back, so `select()` would no longer control what gets copied.
  const activeElement = document.activeElement as Element | null | undefined;
  const container =
    activeElement?.closest?.('[role="dialog"]') ?? document.body;

  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0";
    container.appendChild(textarea);
    textarea.focus();
    textarea.select();
    // If something (e.g. a focus trap) took focus back, the selection is not
    // our text and execCommand would "succeed" while copying the wrong thing.
    if (document.activeElement !== textarea) return false;
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea?.parentNode?.removeChild(textarea);
  }
}
